import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import RfPageFrame from '../layout/RfPageFrame';

export default function SolutionsCopilotReady() {
  return (
    <>
      <div className="rf-hero-band">
        <RfPageFrame className="!py-10 md:!py-14">
          <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5c6d7e] mb-2">Solutions · Human Trust · AgentReady</p>
          <h1 className="text-2xl md:text-[2.25rem] font-semibold text-[#2c2c2c] tracking-tight max-w-3xl leading-tight">
            AgentReady — Human readiness for enterprise AI agent deployments
          </h1>
          <p className="mt-6 text-[1.05rem] text-[#5c5c5c] max-w-2xl leading-relaxed">
            Before you roll out enterprise AI agents — whether that's Microsoft 365 Copilot, Google Workspace AI, Salesforce Einstein, ServiceNow AI, GitHub Copilot, or any other productivity or automation platform — prepare your people. After go-live, keep them sharp with ongoing simulation and measurement.
          </p>
          <span className="inline-block mt-4 text-xs px-3 py-1.5 rounded bg-[#fff8ee] text-[#854F0B] font-medium">Tied to enterprise AI agent rollouts</span>
        </RfPageFrame>
      </div>

      <section className="bg-white border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c] mb-4">Works across every enterprise AI platform</h2>
          <p className="text-[#5c5c5c] mb-10 max-w-3xl leading-relaxed">
            AgentReady is platform-agnostic. The human risks — prompt injection via email, anomalous agent behaviour, over-privileged autonomous actions, data exfiltration through agent integrations — are consistent regardless of which AI platform your organisation deploys. We build readiness that transfers across tools.
          </p>
          <div className="flex flex-wrap gap-3 mb-12">
            {[
              'Microsoft 365 Copilot',
              'Google Workspace AI',
              'Salesforce Einstein',
              'ServiceNow AI',
              'GitHub Copilot',
              'Workday AI',
              'SAP Joule',
              'Slack AI',
              'Notion AI',
              'Custom enterprise agents',
            ].map((platform) => (
              <span key={platform} className="inline-block text-sm px-3 py-1.5 rounded bg-[#f0f6fb] text-[#2c2c2c] border border-[#c5d4e3]">
                {platform}
              </span>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-xl font-semibold text-[#2c2c2c] mb-6">Pre-deployment</h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Role-based Agent Training',
                    desc: 'What your AI agents can and cannot safely do per role. Boundaries for sensitive data access, external sharing, autonomous actions, and multi-agent orchestration risks.',
                    color: '#005a9c',
                  },
                  {
                    title: 'Verification Protocols',
                    desc: 'When and how to question agent output. What anomalous behaviour looks like — unexpected file access, unusual content generation, actions outside normal scope.',
                    color: '#005a9c',
                  },
                  {
                    title: 'Acceptable Use Policy',
                    desc: 'Clear, employee-ready policy covering AI agent use cases, prohibited actions, data handling boundaries, and how to report concerns. Includes management sign-off workflow.',
                    color: '#005a9c',
                  },
                  {
                    title: 'TrustScore Baseline',
                    desc: 'Quantified human readiness score before go-live. Sets the baseline for measuring improvement post-deployment.',
                    color: '#005a9c',
                  },
                ].map(({ title, desc, color }) => (
                  <div key={title} className="flex gap-4">
                    <div className="w-2 h-2 mt-2 rounded-full shrink-0" style={{ background: color }} />
                    <div>
                      <h4 className="font-semibold text-[#2c2c2c] text-[0.9375rem]">{title}</h4>
                      <p className="text-sm text-[#5c5c5c] mt-1 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-[#2c2c2c] mb-6">Post-deployment</h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Ongoing Simulation',
                    desc: 'Quarterly prompt injection and agent manipulation simulations to maintain employee vigilance after go-live. Scenarios adapted to your specific deployed platforms.',
                    color: '#0F6E56',
                  },
                  {
                    title: 'Anomaly Reporting Workflows',
                    desc: 'Embedded reporting mechanism for employees to flag suspicious agent behaviour — feeding directly into your security team for investigation.',
                    color: '#0F6E56',
                  },
                  {
                    title: 'Continuous Measurement',
                    desc: 'TrustScore tracked over time with trend analysis. Demonstrates ROI of readiness investment to leadership and cyber insurers.',
                    color: '#0F6E56',
                  },
                  {
                    title: 'Incident Response Integration',
                    desc: 'Defined escalation paths when an agent anomaly is detected. Who to call, what to document, how to contain — regardless of which platform triggered the incident.',
                    color: '#0F6E56',
                  },
                ].map(({ title, desc, color }) => (
                  <div key={title} className="flex gap-4">
                    <div className="w-2 h-2 mt-2 rounded-full shrink-0" style={{ background: color }} />
                    <div>
                      <h4 className="font-semibold text-[#2c2c2c] text-[0.9375rem]">{title}</h4>
                      <p className="text-sm text-[#5c5c5c] mt-1 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </RfPageFrame>
      </section>

      <section className="bg-[#f0f7ff] border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          <div className="rf-card p-8 md:p-10 bg-white">
            <h2 className="text-lg font-semibold text-[#2c2c2c] mb-4">Why now?</h2>
            <p className="text-[#5c5c5c] leading-relaxed max-w-3xl">
              Enterprise AI agent rollouts are accelerating across every platform and function. Most organisations focus entirely on technical controls — permissions, data labels, DLP policies, OAuth scopes. Almost none prepare their people for what happens when an agent behaves unexpectedly, when it's manipulated via prompt injection in an email, or when it takes an autonomous action nobody authorised. The risk is not in any one platform — it is in the gap between what the technology can do and what employees are trained to catch. AgentReady closes that gap before it becomes an incident.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap gap-6">
            <Link to="/solutions/trustscore" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c] hover:text-[#004a82]">
              TrustScore — baseline assessment <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/solutions/deepstrike" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c] hover:text-[#004a82]">
              DeepStrike — managed simulations <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <p className="mt-10 text-[0.9375rem] text-[#5c5c5c]">
            <Link to="/solutions/security-for-ai" className="text-[#005a9c] font-medium hover:text-[#004a82]">
              ← Back to Security for AI
            </Link>
          </p>
        </RfPageFrame>
      </section>
    </>
  );
}
